import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getAdminOrManagerRoles,
    getCanApproveSelectedOrder,
    getCanMoveToPendingApprovalSelectedOrder,
    getCanMoveToSupervisorReviewSelectedOrder,
} from 'entities/Order/model/selectors/ordersPermissions';
import { getSelectedOrder } from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { getProposalFilterSellersDataBySeller } from 'features/OrderProposalFilter';
import {
    ApproveOrderButton,
    MoveToApprovalOrderButton,
    MoveToPendingOrderButton,
} from 'features/changeOrderStatus';

import { useOrderSubmitUnavailableLabel } from '../../lib/hooks/useOrderSubmitUnavailableLabel';

interface OrderViewPageApproveButtonProps {
    groupedItems: Record<string, CatalogItem[]>;
    isNoItemsInOrder: boolean;
    canBeApproved: boolean;
    hasLoadingPnA: boolean;
}

export const OrderViewPageApproveButton = ({
    groupedItems,
    isNoItemsInOrder,
    canBeApproved,
    hasLoadingPnA,
}: OrderViewPageApproveButtonProps) => {
    const params = useParams();
    const canApprove = useSelector(getCanApproveSelectedOrder);
    const currentProperty = useSelector(getSelectedProperty);
    const currentOrder = useSelector(getSelectedOrder);
    const sellersInProposals = useSelector(
        getProposalFilterSellersDataBySeller,
    );

    const canMoveToPendingApproval = useSelector(
        getCanMoveToPendingApprovalSelectedOrder,
    );

    const canMoveToSupervisor = useSelector(
        getCanMoveToSupervisorReviewSelectedOrder,
    );
    const isManagerOrPMC = useSelector(getAdminOrManagerRoles);

    const isRequiredPoInProperty = useMemo(
        () => !!currentProperty?.isPoNumbersRequired,
        [currentProperty],
    );

    const isRequiredPoNumberForOrder = useMemo(() => {
        const poNumbers = currentOrder?.poNumbers;
        let disabledApproveOrder = false;
        Object.entries(sellersInProposals).forEach(([, sellerData]) => {
            const sellerUid = sellerData?.sellerUid;
            const matchingPo = poNumbers.find((el) => el.id === sellerUid);
            if (!matchingPo?.poNumber) {
                disabledApproveOrder = true;
            }
        });
        return isRequiredPoInProperty && disabledApproveOrder && isManagerOrPMC;
    }, [
        currentOrder,
        isRequiredPoInProperty,
        sellersInProposals,
        isManagerOrPMC,
    ]);

    const { unavailableItemCount, shouldShowUnavailableLabel } =
        useOrderSubmitUnavailableLabel({
            groupedItems,
            isManagerOrPMC,
            isRequiredPoNumberForOrder,
            hasLoadingPnA,
        });

    const orderId = params?.orderId;

    if (canApprove) {
        return (
            <ApproveOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder || !canBeApproved}
                isRequiredPoNumberForOrder={isRequiredPoNumberForOrder}
                hasLoadingPnA={hasLoadingPnA}
                shouldShowUnavailableLabel={shouldShowUnavailableLabel}
                unavailableItemCount={unavailableItemCount}
            />
        );
    }

    if (canMoveToPendingApproval) {
        return (
            <MoveToPendingOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder}
            />
        );
    }

    if (canMoveToSupervisor) {
        return (
            <MoveToApprovalOrderButton
                orderId={orderId}
                isNoItemsInOrder={isNoItemsInOrder}
            />
        );
    }

    return null;
};
